import React, { FC, useCallback, useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Box, Chip, Divider, FormControl, Grid, MenuItem, Select, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { CreateBusinessEntityRequestDto } from '@sparling/financial-common'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { TransparentButton } from '../../../../components/Buttons/TransparentButton'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { InputField } from '../../../../components/InputField'
import { GreenCheckBox } from '../../../../components/CheckBox'
import {
  createBusinessEntitiesRequest,
  getUsersForAssignEntityRequest,
  resetCreatedBusinessEntity,
} from '../../../../store/actions'
import {
  createBusinessEntityData,
  createBusinessEntityLoading,
  getUsersForAssignEntityData,
  usersForAssignEntityLoading,
} from '../../../../store/selectors'
import { Loader } from '../../../../components/Loader'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
  },
  title: {
    lineHeight: 0,
  },
  buttonWrap: {
    justifyContent: 'space-between',
    width: 256,
  },
  formContent: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    margin: '32px 0',
    padding: 32,
    background: 'rgba(216, 216, 216, 0.05)',
    display: 'flex',
    gap: theme.spacing(6),
  },
  inputWrap: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  horizontalLine: {
    backgroundColor: '#767a7e',
    margin: '32px 32px 32px 0',
    width: '100%',
  },
  activeCheckbox: {
    display: 'flex',
    marginTop: 24,
  },
  chip: {
    color: theme.palette.primary.light,
  },
}))

export const CreateBusinessEntity: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const usersForAssignEntityIsLoading = useSelector(usersForAssignEntityLoading)
  const { items } = useSelector(getUsersForAssignEntityData)
  const creating = useSelector(createBusinessEntityLoading)
  const createdEntity = useSelector(createBusinessEntityData)

  const formMethods = useForm<CreateBusinessEntityRequestDto>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver: classValidatorResolver(CreateBusinessEntityRequestDto),
  })
  const { handleSubmit, getValues, control } = formMethods

  const onSubmit = useCallback(() => {
    dispatch(createBusinessEntitiesRequest(getValues()))
  }, [dispatch, getValues])

  useEffect(() => {
    dispatch(getUsersForAssignEntityRequest({}))
  }, [dispatch])

  useEffect(() => {
    if (createdEntity?.id) {
      history.goBack()
      dispatch(resetCreatedBusinessEntity())
    }
  }, [dispatch, history, createdEntity])

  if (creating) return <Loader />

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.topWrap}>
          <Typography variant="h1" className={classes.title}>
            Create Business Entity
          </Typography>
          <Grid container item className={classes.buttonWrap}>
            <TransparentButton width={120} onClick={history.goBack} text="Cancel" />
            <GreenButton type="submit" width={120} text="Save" />
          </Grid>
        </Grid>
        <Grid item container className={classes.formContent}>
          <Typography variant="h3" className={classes.title}>
            General
          </Typography>
          <Grid item container className={classes.inputWrap}>
            <InputField inputName={'name'} labelName={'Name'} inputType={'text'} width={600} />
            <InputField
              inputName={'description'}
              labelName={'Description'}
              inputType={'text'}
              multiline
              rows={3}
              withoutPadding
              width={600}
            />
            <Grid className={classes.activeCheckbox} component={'div'}>
              <GreenCheckBox checkBoxLabel={'Active'} checkBoxName={'active'} status />
            </Grid>
          </Grid>
        </Grid>

        <Divider className={classes.horizontalLine} variant={'fullWidth'} />

        <Grid item container className={classes.formContent}>
          <Typography variant="h3" className={classes.title}>
            Users
          </Typography>
          <Grid item container className={classes.inputWrap}>
            <FormControl style={{ width: 600 }}>
              <Controller
                name="users"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <Select
                    id={'listbox'}
                    {...field}
                    multiple
                    MenuProps={{ style: { maxHeight: 500 } }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => (
                          <Chip
                            key={id}
                            label={items.find((user) => user.id === id)?.name || id}
                            className={classes.chip}
                            onDelete={(event) => {
                              event.stopPropagation()
                              field.onChange(selected.filter((selectedId) => selectedId !== id))
                            }}
                            onMouseDown={(event) => event.stopPropagation()}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {items.map((user) => (
                      <MenuItem key={user.idSf} value={user.id}>
                        {`${user.idSf} ${user.name} `}
                      </MenuItem>
                    ))}
                    {usersForAssignEntityIsLoading && (
                      <MenuItem disabled>
                        <Typography variant="body2">Loading...</Typography>
                      </MenuItem>
                    )}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Divider className={classes.horizontalLine} variant={'fullWidth'} />

        <Grid item container className={classes.formContent}>
          <Grid item container className={classes.inputWrap}>
            <Typography variant="h3">Fireblocks Settings</Typography>
            <InputField inputName={'fireblocks.apiUrl'} labelName={'API URL'} inputType={'text'} width={600} />
            <InputField inputName={'fireblocks.apiKey'} labelName={'API Key'} inputType={'text'} width={600} />
            <InputField
              inputName={'fireblocks.privateKey'}
              labelName={'Private Key'}
              inputType={'text'}
              multiline
              rows={3}
              withoutPadding
              width={600}
            />
          </Grid>
          <Grid item container className={classes.inputWrap}>
            <Typography variant="h3">B2C2 Settings</Typography>
            <InputField inputName={'b2c2.apiToken'} labelName={'API Token'} inputType={'text'} width={600} />
            <InputField inputName={'b2c2.apiUrl'} labelName={'API URL'} inputType={'text'} width={600} />
            <InputField inputName={'b2c2.wsApiUrl'} labelName={'WebSocket API URL'} inputType={'text'} width={600} />
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
